import { useState, useLayoutEffect, useRef } from 'react';

export function useStickyTop(defaultTop = 0, parentScrollRef = null) {
  const [stickyTop, setStickyTop] = useState(defaultTop);
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollContainerRef = useRef(null);

  useLayoutEffect(() => {
    const computeTop = () => {
      setStickyTop(defaultTop);
    };

    // Find the actual scrolling parent
    const findScrollContainer = () => {
      //first priority to parentScrollRef if provided
      if(parentScrollRef && parentScrollRef.current) {
        return parentScrollRef.current;
      }

      //then check the ref provided
      let element = scrollContainerRef.current;
      while (element && element !== document.body) {
        const computed = getComputedStyle(element);
        const hasOverflow = /(auto|scroll|hidden|clip)/.test(computed.overflow + computed.overflowY + computed.overflowX);
        const isOverflowing = element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
        
        if (hasOverflow && isOverflowing) {
          return element; // Found the scrolling parent
        }
        element = element.parentElement;
      }

      // If no scrolling parent found
      return window; // Fallback to window
    };

    const scrollContainer = findScrollContainer();
    let scrollHandler = null;

    if (scrollContainer === window) {
      // Window scroll
      scrollHandler = () => {
        setIsScrolling(window.scrollY > 0);
      };
      window.addEventListener('scroll', scrollHandler, { passive: true });
    } else {
      // Element scroll
      scrollHandler = () => {
        setIsScrolling(scrollContainer.scrollTop > 0);
      };
      scrollContainer.addEventListener('scroll', scrollHandler, { passive: true });
    }

    // Initial state
    if (scrollContainer === window) {
      setIsScrolling(window.scrollY > 0);
    } else {
      setIsScrolling(scrollContainer.scrollTop > 0);
    }

    requestAnimationFrame(computeTop);
    window.addEventListener('resize', computeTop);

    return () => {
      window.removeEventListener('resize', computeTop);
      if (scrollContainer === window) {
        window.removeEventListener('scroll', scrollHandler);
      } else {
        scrollContainer.removeEventListener('scroll', scrollHandler);
      }
    };
  }, [defaultTop, parentScrollRef]);

  return {
    stickyTop,
    isScrolling,
    ref: scrollContainerRef // Attach this ref to the container you want to monitor
  };
}